import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { InfiniteScrollCustomEvent } from '@ionic/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Action, Store } from '@ngrx/store';
import { clearFiltersStore, incrementFilterPage } from 'src/app/data/store/actions/filters.actions';
import { selectFiltersMenuState } from 'src/app/data/store/selectors/filters.selectors';
import { noDataText } from '../../constants';
import { TextSizes, TextType } from '../../enums';
import { distinctUntilChanged, filter, Observable, take } from 'rxjs';
import { UtilsService } from '../../services/utils.service';
import { NavigationStart, Router } from '@angular/router';
import isEqual from '../../utils/isEqual';
import { IonicModule } from '@ionic/angular';

import { TextComponent } from '../text/text.component';

@UntilDestroy()
@Component({
  selector: 'app-pagination-container',
  templateUrl: './pagination-container.component.html',
  styleUrls: ['./pagination-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, TextComponent],
})
export class PaginationContainerComponent implements OnInit {
  private _store = inject(Store);
  private _utils = inject(UtilsService);
  private _cdr = inject(ChangeDetectorRef);
  private _router = inject(Router);

  @Input() isLoading$ = new Observable<boolean | null>();

  @Input() isAllLoaded: boolean | null = true;

  @Input() noDataText?: string = noDataText;

  @Input() paginationData: any[] | null | undefined;

  @Input() isFiltersPagination = false;

  @Input() isOnlyFiltersList = false;

  @Input() hideLoading?: boolean = false;

  @Input() listName?: string;

  @Input() clearListAction?: Action<any>;

  @Output() emitScrolling = new EventEmitter<string | null | undefined>();

  readonly TextSizes = TextSizes;

  readonly TextType = TextType;

  public filtersMenu$ = this._store.select(selectFiltersMenuState);

  public isLoading: boolean | null = false;

  public elementId: string | undefined;

  public ngOnInit(): void {
    this.isLoading$.pipe(untilDestroyed(this)).subscribe((isLoading: boolean | null) => {
      this.isLoading = isLoading;
      this._cdr.detectChanges();
    });

    this.filtersMenu$
      .pipe(
        untilDestroyed(this),
        filter(
          (data) =>
            (data.filters?.filtersMenuParams != null || data.query != null) && !this.isLoading,
        ),
        distinctUntilChanged((prev, curr) => {
          return isEqual(prev, curr);
        }),
      )
      .subscribe((res) => {
        this.isLoading = true;
        setTimeout(() => {
          this.emitScrolling.emit(res.query);
        }, 0);
      });

    this._cdr.detectChanges();
    if (!this.paginationData?.length) {
      this.isLoading = true;
      this._store.dispatch(incrementFilterPage({ initialPage: 0 }));
      this.emitScrolling.emit();
    }
    if (this.listName) {
      this.resetListWithOptions(this.listName, this.clearListAction, !this.isFiltersPagination);
    }

    if (sessionStorage[String(this.listName)]) {
      this.elementId = sessionStorage[String(this.listName)];
    }
  }

  public resetListWithOptions(listName?: string, action?: Action<any>, clearFilters?: boolean) {
    this._router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .pipe(take(1))
      .subscribe((event: NavigationStart) => {
        if (!event.url.includes(String(listName)) && action) {
          if (event.url === '/dashboard/shops' && listName === 'shop-list') {
            return;
          }
          this._store.dispatch(action);
          if (clearFilters) this._store.dispatch(clearFiltersStore());
        }
      });
  }

  public onScroll(event?: Event, initialPage?: number): void {
    (event as InfiniteScrollCustomEvent)?.target.complete();
    if (!this.isAllLoaded && !this.isLoading) {
      this.isLoading = true;
      this._store.dispatch(incrementFilterPage({ initialPage }));
      this.emitScrolling.emit();
    }
  }

  get showLoading() {
    return this.isLoading && !this.hideLoading;
  }
}
